import React, { useEffect, useState, useRef, createElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Input } from '../components/ui/Input';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { FileTextIcon, AlertCircleIcon, CalendarIcon, UserIcon, BuildingIcon, CreditCardIcon, CheckCircleIcon, ClockIcon, XCircleIcon, MailIcon, PhoneIcon, LockIcon, PlusIcon, CheckIcon } from 'lucide-react';
// Declare Stripe types
declare global {
  interface Window {
    Stripe: any;
  }
}
// API invoice item type
interface ApiInvoiceItem {
  title: string;
  qty: number;
  price: number;
  amount: number;
  taxable?: number;
}
// Company object from API
interface ApiCompany {
  name: string;
  email?: string;
  phone?: string;
  stripe_public_key?: string;
  logo?: string;
}
// Saved payment method type
interface PaymentMethod {
  id: string;
  card_brand: string;
  card_exp: string;
  card_last_digits: string;
}
// API invoice response type
interface PublicInvoiceData {
  id: string;
  number: string;
  items: ApiInvoiceItem[];
  amount: number;
  paid_amount: number;
  tax: number;
  tax_label?: string;
  fee?: number;
  fee_label?: string;
  shipping_amount: number;
  discount: number;
  status: string;
  created: number;
  sent_date?: number;
  invoice_date?: number;
  customer_id: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  company?: ApiCompany;
  // Legacy fields (fallback)
  company_name?: string;
  company_email?: string;
  subscription_id?: string;
  subscription_number?: string;
  payment_period_days: number;
  payment_date?: number;
  // Saved payment methods
  payment_methods?: PaymentMethod[];
}
// Helper to get card brand icon/display name
const getCardBrandDisplay = (brand: string): string => {
  const brands: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay'
  };
  return brands[brand.toLowerCase()] || brand;
};
export function PublicInvoiceView() {
  const {
    hash
  } = useParams<{
    hash: string;
  }>();
  const [invoice, setInvoice] = useState<PublicInvoiceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardError, setCardError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  // Selected payment method: null = new card, string = saved method id
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  // Stripe refs
  const stripeRef = useRef<any>(null);
  const cardElementRef = useRef<any>(null);
  const cardMountedRef = useRef(false);
  // Ref for payment form section (for scroll to payment)
  const paymentFormRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!hash) {
        setError('Invalid invoice link');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await callBillingAPI('publicGetInvoice', {
          hash
        });
        console.log('Public invoice response:', response);
        setInvoice(response);
        // Set default payment amount to outstanding balance
        const outstanding = (response.amount || 0) - (response.paid_amount || 0);
        setPaymentAmount(outstanding.toFixed(2));
        // If there are saved payment methods, select the first one by default
        if (response.payment_methods && response.payment_methods.length > 0) {
          setSelectedPaymentMethod(response.payment_methods[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch public invoice:', err);
        setError(err instanceof Error ? err.message : 'Failed to load invoice');
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvoice();
  }, [hash]);
  // Load Stripe script and initialize
  useEffect(() => {
    if (!invoice) return;
    const stripePublicKey = invoice.company?.stripe_public_key;
    if (!stripePublicKey) return;
    // Check if payment is needed
    const canPay = ['sent', 'overdue', 'partial_paid'].includes(invoice.status);
    if (!canPay) return;
    // Load Stripe script if not already loaded
    if (!window.Stripe) {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        initializeStripe(stripePublicKey);
      };
      document.body.appendChild(script);
    } else {
      initializeStripe(stripePublicKey);
    }
    return () => {
      // Cleanup card element on unmount
      if (cardElementRef.current) {
        cardElementRef.current.destroy();
        cardElementRef.current = null;
        cardMountedRef.current = false;
      }
    };
  }, [invoice]);
  // Re-mount card element when switching to new card
  useEffect(() => {
    if (selectedPaymentMethod === null && stripeRef.current && !cardMountedRef.current) {
      const stripePublicKey = invoice?.company?.stripe_public_key;
      if (stripePublicKey) {
        // Small delay to ensure DOM element exists
        setTimeout(() => {
          mountCardElement();
        }, 100);
      }
    }
  }, [selectedPaymentMethod, invoice]);
  const mountCardElement = () => {
    if (!stripeRef.current || cardMountedRef.current) return;
    try {
      const elements = stripeRef.current.elements();
      const card = elements.create('card', {
        style: {
          base: {
            color: '#32325d',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
              color: '#aab7c4'
            }
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        }
      });
      const cardElement = document.getElementById('card-element');
      if (cardElement) {
        card.mount('#card-element');
        cardMountedRef.current = true;
        cardElementRef.current = card;
        card.on('change', (event: any) => {
          setCardComplete(event.complete);
          if (event.error) {
            setCardError(event.error.message);
          } else {
            setCardError(null);
          }
        });
        setStripeLoaded(true);
      }
    } catch (err) {
      console.error('Failed to mount card element:', err);
    }
  };
  const initializeStripe = (stripePublicKey: string) => {
    if (stripeRef.current) return;
    try {
      stripeRef.current = window.Stripe(stripePublicKey);
      // Only mount card if no saved payment methods or new card is selected
      if (!invoice?.payment_methods?.length || selectedPaymentMethod === null) {
        const elements = stripeRef.current.elements();
        const card = elements.create('card', {
          style: {
            base: {
              color: '#32325d',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSmoothing: 'antialiased',
              fontSize: '16px',
              '::placeholder': {
                color: '#aab7c4'
              }
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a'
            }
          }
        });
        // Wait for the element to be in DOM
        const mountCard = () => {
          const cardElement = document.getElementById('card-element');
          if (cardElement && !cardMountedRef.current) {
            card.mount('#card-element');
            cardMountedRef.current = true;
            cardElementRef.current = card;
            card.on('change', (event: any) => {
              setCardComplete(event.complete);
              if (event.error) {
                setCardError(event.error.message);
              } else {
                setCardError(null);
              }
            });
            setStripeLoaded(true);
          } else if (!cardMountedRef.current) {
            // Retry mounting if element not found yet
            setTimeout(mountCard, 100);
          }
        };
        mountCard();
      } else {
        // Stripe is loaded but we're using saved method
        setStripeLoaded(true);
      }
    } catch (err) {
      console.error('Failed to initialize Stripe:', err);
      setCardError('Failed to load payment form');
    }
  };
  const handlePayment = async () => {
    if (!invoice) return;
    const amount = parseFloat(paymentAmount);
    const outstanding = invoice.amount - invoice.paid_amount;
    // Validate amount
    if (isNaN(amount) || amount <= 0) {
      setCardError('Please enter a valid payment amount');
      return;
    }
    if (amount > outstanding) {
      setCardError(`Amount cannot exceed outstanding balance of ${formatCurrency(outstanding)}`);
      return;
    }
    setIsProcessingPayment(true);
    setCardError(null);
    try {
      // Check if using saved payment method
      if (selectedPaymentMethod) {
        // Pay with saved method
        await callBillingAPI('publicPayInvoice', {
          invoice_id: invoice.id,
          amount: amount,
          customer_payment_method_id: selectedPaymentMethod
        });
      } else {
        // Pay with new card
        if (!stripeRef.current || !cardElementRef.current) {
          setCardError('Payment form not loaded. Please refresh and try again.');
          setIsProcessingPayment(false);
          return;
        }
        if (!cardHolderName.trim()) {
          setCardError('Please enter the cardholder name');
          setIsProcessingPayment(false);
          return;
        }
        if (!cardComplete) {
          setCardError('Please complete the card details');
          setIsProcessingPayment(false);
          return;
        }
        // Create Stripe Payment method
        const result = await stripeRef.current.createPaymentMethod({
          type: 'card',
          card: cardElementRef.current,
          billing_details: {
            name: cardHolderName
          }
        });
        if (result.error) {
          setCardError(result.error.message);
          setIsProcessingPayment(false);
          return;
        }
        // Call API to process payment
        await callBillingAPI('publicPayInvoice', {
          invoice_id: invoice.id,
          amount: amount,
          stripe_info: {
            method_id: result.paymentMethod.id,
            card_brand: result.paymentMethod.card.brand,
            card_exp: result.paymentMethod.card.exp_month + '/' + result.paymentMethod.card.exp_year,
            card_digits: result.paymentMethod.card.last4
          }
        });
      }
      // Reload page after successful payment
      window.location.reload();
    } catch (err) {
      console.error('Payment failed:', err);
      setCardError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };
  // Handle selecting a saved payment method
  const handleSelectPaymentMethod = (methodId: string | null) => {
    // If switching to new card, unmount existing card element first
    if (methodId === null && cardElementRef.current) {
      cardElementRef.current.destroy();
      cardElementRef.current = null;
      cardMountedRef.current = false;
    }
    setSelectedPaymentMethod(methodId);
    setCardError(null);
  };
  // Handle amount input - ensure it doesn't exceed outstanding
  const handleAmountChange = (value: string) => {
    if (!invoice) return;
    const outstanding = invoice.amount - invoice.paid_amount;
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) {
      setPaymentAmount(value);
      return;
    }
    // Clamp to outstanding amount
    if (numValue > outstanding) {
      setPaymentAmount(outstanding.toFixed(2));
    } else if (numValue < 0) {
      setPaymentAmount('0');
    } else {
      setPaymentAmount(value);
    }
  };
  // Format date from timestamp
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
      icon: React.ReactNode;
    }> = {
      draft: {
        variant: 'default',
        label: 'Draft',
        icon: <FileTextIcon className="w-3 h-3" data-id="element-1789" />
      },
      sent: {
        variant: 'info',
        label: 'Awaiting Payment',
        icon: <ClockIcon className="w-3 h-3" data-id="element-1790" />
      },
      paid: {
        variant: 'success',
        label: 'Paid',
        icon: <CheckCircleIcon className="w-3 h-3" data-id="element-1791" />
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partially Paid',
        icon: <CreditCardIcon className="w-3 h-3" data-id="element-1792" />
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue',
        icon: <AlertCircleIcon className="w-3 h-3" data-id="element-1793" />
      },
      voided: {
        variant: 'default',
        label: 'Voided',
        icon: <XCircleIcon className="w-3 h-3" data-id="element-1794" />
      },
      future: {
        variant: 'info',
        label: 'Scheduled',
        icon: <CalendarIcon className="w-3 h-3" data-id="element-1795" />
      }
    };
    const config = statusConfig[status] || {
      variant: 'default' as const,
      label: status,
      icon: null
    };
    return <Badge variant={config.variant} className="inline-flex items-center gap-1.5 px-3 py-1" data-id="element-1796">
        {config.icon}
        {config.label}
      </Badge>;
  };
  // Loading state
  if (isLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-1797">
        <Card className="max-w-md w-full text-center py-16" data-id="element-1798">
          <div className="inline-block w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" data-id="element-1799"></div>
          <p className="text-gray-600" data-id="element-1800">Loading invoice...</p>
        </Card>
      </div>;
  }
  // Error state
  if (error || !invoice) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-1801">
        <Card className="max-w-md w-full text-center py-12 px-6" data-id="element-1802">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4" data-id="element-1803">
            <AlertCircleIcon className="w-8 h-8 text-red-600" data-id="element-1804" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1805">
            Invoice Not Found
          </h2>
          <p className="text-gray-600" data-id="element-1806">
            {error || "The invoice you're looking for doesn't exist or the link has expired."}
          </p>
        </Card>
      </div>;
  }
  // Get company info (prefer company object, fallback to legacy fields)
  const companyName = invoice.company?.name || invoice.company_name;
  const companyEmail = invoice.company?.email || invoice.company_email;
  const companyPhone = invoice.company?.phone;
  const companyLogo = invoice.company?.logo;
  const stripePublicKey = invoice.company?.stripe_public_key;
  // Calculate totals
  const subtotal = invoice.items?.reduce((sum, item) => sum + item.qty * item.price, 0) || 0;
  const taxAmount = parseFloat(String(invoice.tax)) || 0;
  const taxLabel = invoice.tax_label || 'Tax';
  const feeAmount = parseFloat(String(invoice.fee)) || 0;
  const feeLabel = invoice.fee_label || 'Fee';
  const shippingAmount = parseFloat(String(invoice.shipping_amount)) || 0;
  const discount = parseFloat(String(invoice.discount)) || 0;
  const totalAmount = parseFloat(String(invoice.amount)) || 0;
  const paidAmount = parseFloat(String(invoice.paid_amount)) || 0;
  const balanceDue = totalAmount - paidAmount;
  // Check if payment form should be shown
  const showPaymentForm = ['sent', 'overdue', 'partial_paid'].includes(invoice.status) && balanceDue > 0 && stripePublicKey;
  // Get saved payment methods
  const savedPaymentMethods = invoice.payment_methods || [];
  const hasSavedMethods = savedPaymentMethods.length > 0;
  // Scroll to payment form
  const scrollToPaymentForm = () => {
    paymentFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // Line items table columns
  const lineItemColumns = [{
    key: 'description',
    header: 'Description',
    render: (item: ApiInvoiceItem) => <span className="font-medium text-gray-900" data-id="element-1807">{item.title}</span>
  }, {
    key: 'quantity',
    header: 'Qty',
    render: (item: ApiInvoiceItem) => <span className="text-gray-600" data-id="element-1808">{item.qty}</span>
  }, {
    key: 'price',
    header: 'Price',
    render: (item: ApiInvoiceItem) => <span className="text-gray-600" data-id="element-1809">{formatCurrency(item.price)}</span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (item: ApiInvoiceItem) => <span className="font-medium text-gray-900" data-id="element-1810">
          {formatCurrency(item.qty * item.price)}
        </span>
  }];
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4" data-id="element-1811">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-1812">
        {/* Invoice Header Card */}
        <Card className="overflow-hidden" data-id="element-1813">
          {/* Status Banner */}
          <div className={`px-6 py-3 ${invoice.status === 'paid' ? 'bg-green-50 border-b border-green-100' : invoice.status === 'overdue' ? 'bg-red-50 border-b border-red-100' : 'bg-blue-50 border-b border-blue-100'}`} data-id="element-1814">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-1815">
              <div className="flex items-center gap-3" data-id="element-1816">
                <FileTextIcon className={`w-5 h-5 flex-shrink-0 ${invoice.status === 'paid' ? 'text-green-600' : invoice.status === 'overdue' ? 'text-red-600' : 'text-blue-600'}`} data-id="element-1817" />
                <span className="font-semibold text-gray-900" data-id="element-1818">
                  Invoice #{invoice.number}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-1819">
                {getStatusBadge(invoice.status)}
                {showPaymentForm && <Button onClick={scrollToPaymentForm} size="sm" leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1821" />} data-id="element-1820">
                    Pay Now
                  </Button>}
              </div>
            </div>
          </div>

          <div className="p-6" data-id="element-1822">
            {/* Company & Customer Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-1823">
              {/* From (Company) */}
              {companyName && <div data-id="element-1824">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2" data-id="element-1825">
                    <BuildingIcon className="w-4 h-4" data-id="element-1826" />
                    From
                  </div>
                  <div className="text-gray-900" data-id="element-1827">
                    <div className="flex items-start gap-3" data-id="element-1828">
                      {companyLogo && <img src={`data:image/png;base64,${companyLogo}`} alt={companyName} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-1829" />}
                      <div data-id="element-1830">
                        <p className="font-semibold text-lg" data-id="element-1831">{companyName}</p>
                        {companyEmail && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-1832">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1833" />
                            {companyEmail}
                          </p>}
                        {companyPhone && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-1834">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1835" />
                            {companyPhone}
                          </p>}
                      </div>
                    </div>
                  </div>
                </div>}

              {/* To (Customer) */}
              <div data-id="element-1836">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2" data-id="element-1837">
                  <UserIcon className="w-4 h-4" data-id="element-1838" />
                  Bill To
                </div>
                <div className="text-gray-900" data-id="element-1839">
                  <p className="font-semibold text-lg" data-id="element-1840">
                    {invoice.customer_name}
                  </p>
                  {invoice.customer_email && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-1841">
                      <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1842" />
                      {invoice.customer_email}
                    </p>}
                  {invoice.customer_phone && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-1843">
                      <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1844" />
                      {invoice.customer_phone}
                    </p>}
                  {invoice.customer_address && <p className="text-sm text-gray-600 mt-1" data-id="element-1845">
                      {invoice.customer_address}
                    </p>}
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-6" data-id="element-1846">
              <div data-id="element-1847">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1848">
                  Invoice Date
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-1849">
                  {invoice.invoice_date ? formatDate(invoice.invoice_date) : invoice.created ? formatDate(invoice.created) : '—'}
                </p>
              </div>
              {invoice.sent_date && <div data-id="element-1850">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1851">
                    Sent Date
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-1852">
                    {formatDate(invoice.sent_date)}
                  </p>
                </div>}
              <div data-id="element-1853">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1854">
                  Payment Terms
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-1855">
                  Net {invoice.payment_period_days} days
                </p>
              </div>
              {invoice.payment_date && <div data-id="element-1856">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1857">
                    Paid On
                  </p>
                  <p className="text-sm font-semibold text-green-600 mt-1" data-id="element-1858">
                    {formatDate(invoice.payment_date)}
                  </p>
                </div>}
            </div>
          </div>
        </Card>

        {/* Line Items */}
        <Card padding="none" data-id="element-1859">
          <div className="p-6 border-b border-gray-200" data-id="element-1860">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1861">Items</h2>
          </div>
          <Table columns={lineItemColumns} data={invoice.items || []} keyExtractor={(_, index) => String(index)} emptyMessage="No items" data-id="element-1862" />

          {/* Totals */}
          <div className="p-6 bg-gray-50 border-t border-gray-200" data-id="element-1863">
            <div className="max-w-xs ml-auto space-y-2" data-id="element-1864">
              <div className="flex justify-between text-sm" data-id="element-1865">
                <span className="text-gray-600" data-id="element-1866">Subtotal</span>
                <span className="text-gray-900" data-id="element-1867">
                  {formatCurrency(subtotal)}
                </span>
              </div>
              {taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-1868">
                  <span className="text-gray-600" data-id="element-1869">{taxLabel}</span>
                  <span className="text-gray-900" data-id="element-1870">
                    {formatCurrency(taxAmount)}
                  </span>
                </div>}
              {feeAmount > 0 && <div className="flex justify-between text-sm" data-id="element-1871">
                  <span className="text-gray-600" data-id="element-1872">{feeLabel}</span>
                  <span className="text-gray-900" data-id="element-1873">
                    {formatCurrency(feeAmount)}
                  </span>
                </div>}
              {shippingAmount > 0 && <div className="flex justify-between text-sm" data-id="element-1874">
                  <span className="text-gray-600" data-id="element-1875">Shipping</span>
                  <span className="text-gray-900" data-id="element-1876">
                    {formatCurrency(shippingAmount)}
                  </span>
                </div>}
              {discount > 0 && <div className="flex justify-between text-sm" data-id="element-1877">
                  <span className="text-gray-600" data-id="element-1878">Discount</span>
                  <span className="text-green-600" data-id="element-1879">
                    -{formatCurrency(discount)}
                  </span>
                </div>}
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200" data-id="element-1880">
                <span className="text-gray-900" data-id="element-1881">Total</span>
                <span className="text-gray-900" data-id="element-1882">
                  {formatCurrency(totalAmount)}
                </span>
              </div>
              {paidAmount > 0 && <div className="flex justify-between text-sm" data-id="element-1883">
                  <span className="text-gray-600" data-id="element-1884">Paid</span>
                  <span className="text-green-600" data-id="element-1885">
                    -{formatCurrency(paidAmount)}
                  </span>
                </div>}
              {balanceDue > 0 && <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200" data-id="element-1886">
                  <span className="text-gray-900" data-id="element-1887">Balance Due</span>
                  <span className="text-red-600" data-id="element-1888">
                    {formatCurrency(balanceDue)}
                  </span>
                </div>}
            </div>
          </div>
        </Card>

        {/* Payment Status Card */}
        {invoice.status === 'paid' ? <Card className="bg-green-50 border-green-200" data-id="element-1889">
            <div className="flex items-center gap-4" data-id="element-1890">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1891">
                <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-1892" />
              </div>
              <div data-id="element-1893">
                <h3 className="font-semibold text-green-900" data-id="element-1894">
                  {totalAmount === 0 ? 'No Payment Required' : 'Payment Complete'}
                </h3>
                <p className="text-sm text-green-700" data-id="element-1895">
                  {totalAmount === 0 ? 'This invoice has a zero balance. No payment is required.' : 'This invoice has been paid in full. Thank you for your payment!'}
                </p>
              </div>
            </div>
          </Card> : invoice.status === 'overdue' ? <Card className="bg-red-50 border-red-200" data-id="element-1896">
            <div className="flex items-center gap-4" data-id="element-1897">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center" data-id="element-1898">
                <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1899" />
              </div>
              <div data-id="element-1900">
                <h3 className="font-semibold text-red-900" data-id="element-1901">Payment Overdue</h3>
                <p className="text-sm text-red-700" data-id="element-1902">
                  This invoice is past due. Please make payment as soon as
                  possible.
                </p>
              </div>
            </div>
          </Card> : invoice.status === 'sent' || invoice.status === 'partial_paid' ? <Card className="bg-blue-50 border-blue-200" data-id="element-1903">
            <div className="flex items-center gap-4" data-id="element-1904">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1905">
                <CreditCardIcon className="w-6 h-6 text-blue-600" data-id="element-1906" />
              </div>
              <div data-id="element-1907">
                <h3 className="font-semibold text-blue-900" data-id="element-1908">
                  Payment Required
                </h3>
                <p className="text-sm text-blue-700" data-id="element-1909">
                  {balanceDue > 0 ? `Amount due: ${formatCurrency(balanceDue)}` : 'Please complete payment for this invoice.'}
                </p>
              </div>
            </div>
          </Card> : null}

        {/* Payment Form */}
        {showPaymentForm && <div ref={paymentFormRef} data-id="element-1910">
            <Card data-id="element-1911">
              <div className="flex items-center gap-3 mb-6" data-id="element-1912">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1913">
                  <CreditCardIcon className="w-5 h-5 text-blue-600" data-id="element-1914" />
                </div>
                <div data-id="element-1915">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-1916">
                    Make a Payment
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1917">
                    Secure payment powered by Stripe
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1918">
                {/* Payment Amount */}
                <div data-id="element-1919">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1920">
                    Payment Amount
                  </label>
                  <div className="relative" data-id="element-1921">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-1922">
                      $
                    </span>
                    <input type="number" min="0.01" max={balanceDue} step="0.01" value={paymentAmount} onChange={e => handleAmountChange(e.target.value)} className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" placeholder="0.00" data-id="element-1923" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1924">
                    Outstanding balance: {formatCurrency(balanceDue)}
                  </p>
                </div>

                {/* Payment Method Selection */}
                {hasSavedMethods && <div data-id="element-1925">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1926">
                      Payment Method
                    </label>
                    <div className="space-y-2" data-id="element-1927">
                      {/* Saved payment methods */}
                      {savedPaymentMethods.map(method => <button key={method.id} type="button" onClick={() => handleSelectPaymentMethod(method.id)} className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-colors ${selectedPaymentMethod === method.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-1928">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === method.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} data-id="element-1929">
                            {selectedPaymentMethod === method.id && <CheckIcon className="w-3 h-3 text-white" data-id="element-1930" />}
                          </div>
                          <CreditCardIcon className="w-5 h-5 text-gray-400" data-id="element-1931" />
                          <div className="flex-1 text-left" data-id="element-1932">
                            <span className="font-medium text-gray-900" data-id="element-1933">
                              {getCardBrandDisplay(method.card_brand)} ••••{' '}
                              {method.card_last_digits}
                            </span>
                            <span className="text-sm text-gray-500 ml-2" data-id="element-1934">
                              Exp {method.card_exp}
                            </span>
                          </div>
                        </button>)}

                      {/* New card option */}
                      <button type="button" onClick={() => handleSelectPaymentMethod(null)} className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-colors ${selectedPaymentMethod === null ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-1935">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === null ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} data-id="element-1936">
                          {selectedPaymentMethod === null && <CheckIcon className="w-3 h-3 text-white" data-id="element-1937" />}
                        </div>
                        <PlusIcon className="w-5 h-5 text-gray-400" data-id="element-1938" />
                        <span className="font-medium text-gray-900" data-id="element-1939">
                          Use a new card
                        </span>
                      </button>
                    </div>
                  </div>}

                {/* New Card Form - only show if no saved methods or new card is selected */}
                {(selectedPaymentMethod === null || !hasSavedMethods) && <>
                    {/* Cardholder Name */}
                    <div data-id="element-1940">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1941">
                        Cardholder Name
                      </label>
                      <input type="text" value={cardHolderName} onChange={e => setCardHolderName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" placeholder="Name on card" data-id="element-1942" />
                    </div>

                    {/* Stripe Card Element */}
                    <div data-id="element-1943">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1944">
                        Card Details
                      </label>
                      <div id="card-element" className="px-4 py-3 border border-gray-300 rounded-lg bg-white min-h-[44px]" data-id="element-1945" />
                      {!stripeLoaded && <div className="flex items-center gap-2 mt-2 text-sm text-gray-500" data-id="element-1946">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1947"></div>
                          Loading payment form...
                        </div>}
                    </div>
                  </>}

                {/* Error Message */}
                {cardError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-id="element-1948">
                    <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1949" />
                    <p className="text-sm text-red-600" data-id="element-1950">{cardError}</p>
                  </div>}

                {/* Submit Button */}
                <Button onClick={handlePayment} disabled={isProcessingPayment || !selectedPaymentMethod && !stripeLoaded} isLoading={isProcessingPayment} className="w-full" leftIcon={!isProcessingPayment ? <LockIcon className="w-4 h-4" data-id="element-1952" /> : undefined} data-id="element-1951">
                  {isProcessingPayment ? 'Processing...' : `Pay ${formatCurrency(parseFloat(paymentAmount) || 0)}`}
                </Button>

                {/* Security Note */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-1953">
                  <LockIcon className="w-3.5 h-3.5" data-id="element-1954" />
                  <span data-id="element-1955">Your payment is secure and encrypted</span>
                </div>
              </div>
            </Card>
          </div>}

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 py-4" data-id="element-1956">
          <p data-id="element-1957">
            Questions about this invoice? Contact {companyEmail || 'the sender'}
            .
          </p>
        </div>
      </div>
    </div>;
}